import { Star } from 'lucide-react'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxValue,
} from '~/components/ui/combobox'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip'
import { useFieldContext } from '~/hooks/form-hooks'
import { Field, type FieldProps } from './field'

type MultiComboboxFieldProps<TValue> = Omit<FieldProps, 'children' | 'error' | 'id'> & {
  items: { label: React.ReactNode; value: TValue }[]
  emptyLabel?: string
  placeholder?: string
  lockedValues?: TValue[]
  lockedTooltip?: string
  disabled?: boolean
}

export function MultiComboboxField<TValue>({
  label,
  optional,
  required,
  className,
  items,
  labelClassName,
  emptyLabel,
  placeholder,
  lockedValues,
  lockedTooltip,
  disabled = false,
}: MultiComboboxFieldProps<TValue>) {
  const field = useFieldContext<TValue[] | null>()

  const errorMessage = (field.state.meta.errors as { message: string }[]).at(0)?.message

  const renderLockedSlot = () => {
    const icon = (
      <span
        aria-label={lockedTooltip}
        className="flex h-full shrink-0 items-center px-1.5 text-[#F39200]"
        data-slot="combobox-chip-locked"
      >
        <Star className="size-3.5 fill-current" />
      </span>
    )

    if (!lockedTooltip) return icon

    return (
      <Tooltip>
        <TooltipTrigger render={icon} />
        <TooltipContent side="top">{lockedTooltip}</TooltipContent>
      </Tooltip>
    )
  }

  type Item = (typeof items)[number]
  return (
    <Field
      id={field.name}
      label={label}
      error={errorMessage}
      optional={optional}
      required={required}
      className={className}
      labelClassName={labelClassName}
    >
      <Combobox
        items={items}
        multiple
        disabled={disabled}
        onValueChange={(v) => field.handleChange(v?.map((item) => item.value) ?? [])}
        value={items.filter((item) => field.state.value?.includes(item.value))}
      >
        <ComboboxChips>
          <ComboboxValue>
            {(values: Item[]) => (
              <>
                {values.map((item) => {
                  const isLocked = lockedValues?.includes(item.value) === true
                  return (
                    <ComboboxChip
                      key={`combobox-chip-${item.value}`}
                      removable={!isLocked}
                      lockedSlot={isLocked ? renderLockedSlot() : undefined}
                    >
                      {item.label}
                    </ComboboxChip>
                  )
                })}
                <ComboboxInput
                  placeholder={
                    values.length > 0 ? undefined : (placeholder ?? 'Selectionner une option')
                  }
                  className="w-full rounded-lg"
                />
              </>
            )}
          </ComboboxValue>
        </ComboboxChips>

        <ComboboxPopup>
          <ComboboxEmpty>{emptyLabel ?? 'Aucune option trouvée'}</ComboboxEmpty>
          <ComboboxList>
            {(item: Item) => (
              <ComboboxItem key={`combobox-item-${item.value}`} value={item}>
                {item.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxPopup>
      </Combobox>
    </Field>
  )
}
